/**
 * v8.3.0 P3-08（T22）：前端权限点常量。
 *
 * 【唯一要求：字符串必须与后端一字不差】
 * 后端唯一事实源是 `server/src/common/permissions.ts` 的 `Permission` 枚举：
 *   price.override / refund.create / order.void / inventory.adjust /
 *   report.view / account.manage / data.backup
 * 前端这里只做「名字的转写」，不复制任何角色 → 权限的矩阵：
 * 权限由服务端在登录响应与 GET /auth/profile 里下发，前端只消费结果。
 * 一旦前端自己也维护一份角色矩阵，就会变成第二份事实源，与后端分叉后
 * 出现「前端显示有权限、后端 403」或反之的静默错位。
 *
 * 所以本文件【故意不包含】ROLE_PERMISSIONS。角色 → 权限的答案只有一个来源：
 * 服务端返回的 permissions 数组。
 */

/** 改价 / 优惠让价 */
export const PRICE_OVERRIDE = 'price.override';
/** 发起退款 */
export const REFUND_CREATE = 'refund.create';
/** 作废订单 */
export const ORDER_VOID = 'order.void';
/** 库存调整（报损、盘点差异、手工调整） */
export const INVENTORY_ADJUST = 'inventory.adjust';
/** 查看经营报表 */
export const REPORT_VIEW = 'report.view';
/** 账号与员工管理 */
export const ACCOUNT_MANAGE = 'account.manage';
/** 数据备份与恢复 */
export const DATA_BACKUP = 'data.backup';

/** 全部权限点（按后端枚举声明顺序），用于遍历 / 校验 / 调试 */
export const ALL_PERMISSIONS = Object.freeze([
  PRICE_OVERRIDE,
  REFUND_CREATE,
  ORDER_VOID,
  INVENTORY_ADJUST,
  REPORT_VIEW,
  ACCOUNT_MANAGE,
  DATA_BACKUP,
] as const);

/** 权限点字面量类型（由 ALL_PERMISSIONS 推导，改名会同步报错） */
export type Permission = (typeof ALL_PERMISSIONS)[number];

/**
 * 命名空间式聚合：给需要 `PERMISSIONS.PRICE_OVERRIDE` 写法的调用方用。
 * 值全部指向上面同一批常量，不存在第二份字符串。
 */
export const PERMISSIONS = Object.freeze({
  PRICE_OVERRIDE,
  REFUND_CREATE,
  ORDER_VOID,
  INVENTORY_ADJUST,
  REPORT_VIEW,
  ACCOUNT_MANAGE,
  DATA_BACKUP,
} as const);

/**
 * 把任意来源（登录响应 / profile / localStorage 旧缓存）里的 permissions
 * 收敛成干净字符串数组。
 *
 * 语义刻意区分「缺失」与「空」：
 * - 返回 null：字段不存在 / 不是数组 → 说明这是 v8.2.5 的老缓存或老后端，
 *   前端【不能】把它当成「收银员（空权限）」，必须去 GET /auth/profile 取真值。
 * - 返回 []：字段存在且为空数组 → 服务端明确说了「这个角色没有任何权限点」，
 *   照实当成空即可（收银员就是这种情况）。
 */
export function normalizePermissions(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.filter((p): p is string => typeof p === 'string' && p.length > 0);
}
